import { createSlice, createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { getRequest } from "../config";

let moduleContent = {
	'kpi': {
		heading: 'KPI',
		aiEnhanced: true,
		uploadFiles: false,
		subheading: 'Key Performance Indicators'
	}, 'repository': {
		aiEnhanced: false,
		uploadFiles: true,
		heading: 'Repository',
		subheading: 'Securely store all your important documents here'
	}, 'business-audit-investment': {
		aiEnhanced: true,
		uploadFiles: false,
		heading: 'Business Audit',
		subheading: 'Get expert recommendations on how to reduce risk'
	}, 'business-audit-growth': {
		aiEnhanced: true,
		uploadFiles: false,
		heading: 'Business Audit',
		subheading: 'Get expert recommendations on how to reduce risk'
	}, 'business-audit-exit': {
		aiEnhanced: true,
		uploadFiles: false,
		heading: 'Business Audit',
		subheading: 'Get expert recommendations on how to reduce risk'
	}, 'directors-questionnaire': {
		aiEnhanced: false,
		uploadFiles: true,
		heading: 'Directors Questionnaire',
		subheading: ''
	}, 'two-minute-test-exit': {
		aiEnhanced: true,
		uploadFiles: false,
		heading: 'Exit Ready Test',
		subheading: 'See How Exit Ready You Are'
	}, 'two-minute-test-growth': {
		aiEnhanced: true,
		uploadFiles: false,
		heading: 'Growth Ready Test',
		subheading: 'See How Growth Ready You Are'
	}, 'two-minute-test-investment': {
		aiEnhanced: true,
		uploadFiles: false,
		heading: 'Investment Ready Test',
		subheading: 'How Investment Ready Are You'
	}, 'esg': {
		aiEnhanced: false,
		uploadFiles: true,
		heading: 'Environmental, Social & Governmental',
		subheading: 'Get expert recommendations on how to reduce risk'
	}, 'software-development': {
		aiEnhanced: false,
		uploadFiles: true,
		heading: 'Software Development',
		subheading: 'Get expert recommendations on how to reduce risk'
	}, 'warranty-disclosure': {
		aiEnhanced: false,
		uploadFiles: true,
		heading: 'Warranty Disclosure Schedule',
		subheading: 'Get expert recommendations on how to reduce risk'
	}, 'unaudited-certification': {
		sections: [],
		heading: 'Certification',
		subheading: 'This is your current certification',
	}, 'investor-lead': {
		uploadFiles: true,
		heading: 'Investor Match',
		subheading: 'Securely store all your important documents here'
	}
}

function matchesSlug(module, slug) {
	if (!module || !slug) return false;
	const s = slug.toLowerCase();
	if (module.slug?.toLowerCase() === s) return true;
	if (module.moduleKey?.toLowerCase() === s) return true;
	if (module.type?.toLowerCase() === s) return true;
	const normalized = (module.moduleName || '')
		.trim().toLowerCase()
		.replace(/[^a-z0-9\s]/g, '')
		.replace(/\s+/g, '-');
	if (normalized === s) return true;
	if (normalized.replace(/-/g, '') === s) return true;
	return false;
}

export const fetchTempModuleData = createAsyncThunk(
	"company/fetchTempModuleData",
	async (data, { rejectWithValue }) => {
		try {
			const { companyId, moduleSlug } = data;
			if (!companyId || !moduleSlug) {
				return rejectWithValue("companyId and moduleSlug are required");
			}

			const response = await getRequest(
				`/modules/dashboard-cards?companyId=${companyId}&modules=true&sections=true&questions=true&answers=true`
			);

			const cards = response?.body?.cards;
			if (!Array.isArray(cards)) {
				return rejectWithValue("Failed to load module data.");
			}

			let matchedModule = null;
			for (const card of cards) {
				for (const m of card.modules || []) {
					if (matchesSlug(m, moduleSlug)) {
						matchedModule = m;
						break;
					}
				}
				if (matchedModule) break;
			}

			if (!matchedModule) {
				return rejectWithValue(`Module "${moduleSlug}" not found.`);
			}

			const sections = matchedModule.sections || [];
			sections.forEach(sec => {
				(sec.questions || []).forEach(q => {
					const ans = (q.answers || [])[0];
					q.answer = ans || {};
				});
			});

			return {
				module: matchedModule,
				sections,
				cards,
			};
		} catch (error) {
			console.log(error, "fetchTempModuleData error");
			return rejectWithValue(error?.response?.data || "Something went wrong");
		}
	}
);

export const fetchCompanyData = createAsyncThunk(
	"company/fetchCompanyData",
	async (data, { rejectWithValue }) => {
		try {
			const { moduleName, companyId, selectedCompany } = data;
			let baseUrl = `/company/get/${companyId}`
			if (moduleName) {
				baseUrl += `?moduleName=${moduleName}`
			}
			// Replace with your API endpoint
			const response = await getRequest(baseUrl);

			if (response['code'] == 200) {
				response['body']['modules']['kpi'] = { type: "kpi", sections: [] };
				for (let module of Object.values(response['body']['modules'])) {
					let content = module['type'].includes('directors-questionnaire') ? moduleContent['directors-questionnaire'] : moduleContent[module['type']]
					if (content) {
						module['heading'] = module['director'] ? `${content['heading']} - ${module['director']['name']} (${module['director']['designation']})` : content['heading'];
						module['aiEnhanced'] = content['aiEnhanced'];
						module['subheading'] = content['subHeading'];
						module['uploadFiles'] = content['uploadFiles'];
					}
				}
				response['body']['modules']['unaudited-certification'] = moduleContent['unaudited-certification'];
				if (!moduleName && selectedCompany?.['modules']) {
					response['body']['modules'] = selectedCompany['modules']
				}
				return response['body']
			};


			return response.body; // Returning the API response
		} catch (error) {
			console.log(error, "error")
			return rejectWithValue(error.response.data || "Something went wrong");
		}
	}
);

export default function getCompanyReducer(company = {}) {
	const companySlice = createSlice({
		name: "company",
		initialState: {
			company,
			selectedCompany: {},
			tempModule: null,
			tempModuleSections: [],
			tempModuleCards: [],
			tempModuleLoading: false,
			tempModuleError: null,
		},
	reducers: {
		updateCompany: (state, action) => {
			return { ...state, ...action.payload };
		},
		selectedCompany: (state, action) => {
			return {
				...state,
				selectedCompany: action.payload
			};
		},
		setFirstVisit: (state, action) => {
			return {
				...state,
				selectedCompany: {
					...state.selectedCompany,
					firstVisitRepository: action.payload
				}
			};
		},
		updateTempModuleAnswers: (state, action) => {
			const savedQuestions = action.payload;
			if (!Array.isArray(savedQuestions) || !state.tempModuleSections) return;

			const answerMap = new Map();
			for (const sq of savedQuestions) {
				if (sq._id) answerMap.set(sq._id.toString(), sq.answer);
			}

			state.tempModuleSections = state.tempModuleSections.map(section => ({
				...section,
				questions: (section.questions || []).map(q => {
					const updatedAnswer = answerMap.get(q._id?.toString());
					if (updatedAnswer) return { ...q, answer: updatedAnswer };
					return q;
				})
			}));
		},
	},
		extraReducers: (builder) => {
			builder
				.addCase(fetchCompanyData.pending, (state) => {
					state.loading = true;
					state.error = null;
				})
				.addCase(fetchCompanyData.fulfilled, (state, action) => {
					state.loading = false;
					state.selectedCompany = action.payload;
				})
				.addCase(fetchCompanyData.rejected, (state, action) => {
					state.loading = false;
					state.error = action.payload;
				})
				.addCase(fetchTempModuleData.pending, (state) => {
					state.tempModuleLoading = true;
					state.tempModuleError = null;
				})
				.addCase(fetchTempModuleData.fulfilled, (state, action) => {
					state.tempModuleLoading = false;
					state.tempModule = action.payload.module;
					state.tempModuleSections = action.payload.sections;
					state.tempModuleCards = action.payload.cards;
					state.tempModuleError = null;
				})
				.addCase(fetchTempModuleData.rejected, (state, action) => {
					state.tempModuleLoading = false;
					state.tempModuleError = action.payload;
				});
		},
	});

	return companySlice.reducer;
}

export const updateCompany = createAction("company/updateCompany");
export const setCompany = createAction("company/selectedCompany");
export const setFirstVisit = createAction("company/setFirstVisit");
export const updateTempModuleAnswers = createAction("company/updateTempModuleAnswers");